<template>
  <div class="app-root" :dir="direction">
    <Toast />
    <ConfirmDialog />
    <router-view />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, nextTick } from 'vue'
import { loadColorScheme } from './themes/theme-helper'
import { AppIdentity } from './ptfm/core/base/identity'
import './themes/shared/rtl.css'
import './themes/shared/layout.css'

// Theme, direction, and theme mode states
const themeLayout = ref('default')
const direction = ref('ltr') //ltr, rtl
const colorScheme = ref('default') // default, ocean, royal-blue
const themeMode = ref('light') // 'light' or 'dark'

// Apply preferences from a data object
const applyPreferences = (data: { themeLayout?: string; uiDirection?: string; colorScheme?: string; themeMode?:string;}) => {
  if (data.themeLayout) {
    themeLayout.value = data.themeLayout
  }
  if (data.uiDirection) {
    direction.value = data.uiDirection
  }
  if (data.colorScheme) {
    colorScheme.value = data.colorScheme   
  } 

  if (data.themeMode) {
    // Map colorScheme to themeMode (colorScheme can be 'light', 'dark', or 'system')
    if (data.colorScheme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      themeMode.value = prefersDark ? 'dark' : 'light'
    } else {
      themeMode.value = data.themeMode
    }
  }
}

// Get user preferences from localStorage, with preview override from sessionStorage
const getUserPreferences = () => {
  try {
    // First check for preview preferences in sessionStorage
    const previewData = sessionStorage.getItem('preferencesPreview')
    if (previewData) {
      const userData = JSON.parse(previewData)
      applyPreferences(userData)
      //console.log('Applied preview preferences:', userData)
      return
    }
    
    // Second, check localStorage userData
    const storedUserData = localStorage.getItem('userData')
    if (storedUserData) {
      const savedUserData = JSON.parse(storedUserData)
      if (savedUserData.uiDirection || savedUserData.colorScheme || savedUserData.theme) {
        applyPreferences(savedUserData)
        //console.log('Applied localStorage userData preferences:', savedUserData)
        return
      }
    }
    
    // Third, fall back to Identity (stored in _idty key)
    const identity = AppIdentity.getActiveIdentity()
    if (identity) {
      applyPreferences({
        themeLayout: identity.themeLayout,
        uiDirection: identity.uiDirection,
        colorScheme: identity.colorScheme,
        themeMode:identity.themeMode
      })
      console.log('Applied Identity preferences:', {
        theme: identity.themeLayout,
        uiDirection: identity.uiDirection,
        colorScheme: identity.colorScheme,
        themeMode:identity.themeMode
      })
      return
    }
    
    console.log('No preferences found, using defaults')
  } catch (error) {
    console.error('Error loading user preferences:', error)
  }
}

// Watch for theme changes and update document
watch(themeLayout, (newThemeLayout) => {
  document.documentElement.setAttribute('data-theme', newThemeLayout)
})

// Watch for direction changes and update document
watch(direction, (newDirection) => {
  document.documentElement.setAttribute('dir', newDirection)
  document.body.setAttribute('dir', newDirection)
  
  // Toggle RTL classes on app root element
  nextTick(() => {
    const appRoot = document.querySelector('.app-root')
    if (appRoot) {
      if (newDirection === 'rtl') {
        appRoot.classList.remove('locale--is-ltr')
        appRoot.classList.add('locale--is-rtl')
      } else {
        appRoot.classList.remove('locale--is-rtl')
        appRoot.classList.add('locale--is-ltr')
      }
    }
  })
  
  console.log('Direction changed to:', newDirection)
})

// Watch for theme mode changes and update document
watch(colorScheme, (newColorScheme) => {
  document.documentElement.setAttribute('data-color-scheme', newColorScheme)
})

// Watch for theme mode changes and update document
watch(themeMode, (newThemeMode) => {
  document.documentElement.setAttribute('data-theme-mode', newThemeMode)
})

// Initialize on mount
onMounted(async () => {
  // First try to get from user preferences
  getUserPreferences()
  
  // Load color scheme CSS dynamically
  try {
    await loadColorScheme(colorScheme.value)
  } catch (error) {
    console.error('Failed to load color scheme:', error)
  }
  
  // Log the loaded preferences for debugging
  console.log('Loaded preferences:', {
    themeLayout: themeLayout.value,
    direction: direction.value,
    colorScheme: colorScheme.value,
    themeMode: themeMode.value
  })
  
  // Apply direction to document
  document.documentElement.setAttribute('dir', direction.value)
  document.body.setAttribute('dir', direction.value)
  
  // Set initial locale classes
  nextTick(() => {
    const appRoot = document.querySelector('.app-root')
    if (appRoot) {
      if (direction.value === 'rtl') {
        appRoot.classList.remove('locale--is-ltr')
        appRoot.classList.add('locale--is-rtl')
      } else {
        appRoot.classList.remove('locale--is-rtl')
        appRoot.classList.add('locale--is-ltr')
      }
    }
  })
  
  // If no user preferences for theme mode or colorScheme, use system preference
  const storedUserData = localStorage.getItem('userData')
  const parsedData = storedUserData ? JSON.parse(storedUserData) : null
  if (!parsedData || (!parsedData.themeMode)) {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    themeMode.value = prefersDark ? 'dark' : 'light'
    document.documentElement.setAttribute('data-theme-mode', themeMode.value)
  }
  
  // Watch system theme changes
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    // Only apply system preference if user has colorScheme set to 'system' or hasn't set a preference
    const userData = localStorage.getItem('userData')
    const parsedData = userData ? JSON.parse(userData) : null
    
    if (!parsedData || !parsedData.colorScheme || parsedData.colorScheme === 'system') {
      const newThemeMode = e.matches ? 'dark' : 'light'
      colorScheme.value = newThemeMode
      document.documentElement.setAttribute('data-theme-mode', newThemeMode)
    }
  })
  
  // Listen for changes to localStorage (for user preferences)
  window.addEventListener('storage', (event) => {
    if (event.key === 'userData') {
      console.log('User preferences changed in localStorage')
      getUserPreferences()
    }
  })
  
  // Create a custom event for preference changes within the same window
  window.addEventListener('user-preferences-changed', () => {
    console.log('User preferences changed event received')
    getUserPreferences()
  })
})
</script>

